import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";
import { X, Megaphone, MessageSquare, MessageCircle, Mail, Handshake, Send, Copy, Check } from "lucide-react";

const STATUS_LABELS = {
  ordered: "Ordered",
  in_transit_china: "In Transit (China)",
  in_transit_djibouti: "In Transit (Djibouti)",
  cleared_djibouti: "Cleared Djibouti",
  in_transit_ethiopia: "In Transit (Ethiopia)",
  delivered: "Arrived in Ethiopia",
};

const AdvertiseModal = ({ shipment, onClose }) => {
  const { API, authHeader } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(
    `Goldspot Trade — Goods Available for Sale

Shipment: ${shipment.shipment_name}
${shipment.container_number ? `Container: ${shipment.container_number}\n` : ""}Status: ${STATUS_LABELS[shipment.status] || shipment.status}

Contact us for pricing and availability.`
  );

  useEffect(() => {
    axios.get(`${API}/trade/brokers`, { headers: authHeader() })
      .then(res => setBrokers(res.data.filter(b => (b.broker_type || "sales") === "sales")))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const encoded = encodeURIComponent(message);
  const waNumber = (num) => (num || "").replace(/[^0-9]/g, "");
  const tgLink = (t) => {
    const v = (t || "").trim();
    if (!v) return "";
    if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
    return `https://t.me/+${v.replace(/[^0-9]/g, "")}`;
  };
  const [copied, setCopied] = useState(false);
  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="advertise-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
              <Megaphone className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif font-semibold text-lg">Advertise to Brokers</h3>
              <p className="text-xs text-white/50">{shipment.shipment_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white transition-colors"
            data-testid="advertise-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-widest text-white/40">Your message</p>
          <button
            onClick={copyMessage}
            className="flex items-center gap-1 text-xs trade-gold hover:underline"
            data-testid="advertise-copy-btn"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={6}
          className="trade-input rounded-sm mb-6"
          data-testid="advertise-message-input"
        />

        <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
          One-tap send ({brokers.length} broker{brokers.length === 1 ? "" : "s"})
        </p>

        {loading ? (
          <div className="text-center p-8 text-white/40 text-sm">Loading brokers...</div>
        ) : brokers.length === 0 ? (
          <div className="text-center p-8 border border-white/5 rounded-sm">
            <Handshake className="w-10 h-10 trade-gold mx-auto mb-3 opacity-40" strokeWidth={1} />
            <p className="text-white/50 text-sm mb-4">No brokers saved yet</p>
            <Link
              to="/trade/brokers/new"
              className="text-xs trade-gold hover:underline"
              data-testid="advertise-add-broker-link"
            >
              Add your first broker
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {brokers.map(b => (
              <div
                key={b.id}
                className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-sm"
                data-testid={`advertise-broker-${b.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{b.name}</p>
                  {b.city && <p className="text-[11px] text-white/40 truncate">{b.city}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {b.phone && (
                    <a
                      href={`sms:${b.phone}?&body=${encoded}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`advertise-text-${b.id}`}
                    >
                      <MessageSquare size={12} /> Text
                    </a>
                  )}
                  {(b.whatsapp || b.phone) && (
                    <a
                      href={`https://wa.me/${waNumber(b.whatsapp || b.phone)}?text=${encoded}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                      data-testid={`advertise-whatsapp-${b.id}`}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                  {b.email && (
                    <a
                      href={`mailto:${b.email}?subject=${encodeURIComponent(`Goods available — ${shipment.shipment_name}`)}&body=${encoded}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`advertise-email-${b.id}`}
                    >
                      <Mail size={12} /> Email
                    </a>
                  )}
                  {b.telegram && (
                    <a
                      href={tgLink(b.telegram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#24A1DE]/10 text-[#24A1DE] hover:bg-[#24A1DE]/20 transition-colors"
                      data-testid={`advertise-telegram-${b.id}`}
                    >
                      <Send size={12} /> Telegram
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdvertiseModal;
